import { config } from '../config';
import { sessionToken as readToken, saveSessionForServer } from './accountSession';
export interface Account {
  id: string;
  name: string;
  rating: number;
  games: number;
  wins: number;
  email?: string;
  firstName?: string;
  lastName?: string;
}
// A ?server= override must never receive another server's bearer token.
export function sessionToken() {
  return readToken(config.serverUrl);
}
export function saveSession(token: string) {
  saveSessionForServer(config.serverUrl, token);
  window.dispatchEvent(new Event('arena-account-changed'));
}
export async function accountRequest<T>(path: string, body?: unknown): Promise<T> {
  if (!config.serverUrl) throw new Error('Online accounts require a game server');
  let response: Response;
  try {
    response = await fetch(`${config.serverUrl}/api/${path}`, {
      method: body === undefined ? 'GET' : 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(sessionToken() ? { Authorization: `Bearer ${sessionToken()}` } : {}),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: AbortSignal.timeout(30000),
    });
  } catch (error) {
    // What the browser says ('TypeError: Failed to fetch', 'TimeoutError: signal timed out') is
    // no sentence for a player (client-tablets-ui:quality#5).
    throw new Error(
      error instanceof DOMException &&
        (error.name === 'TimeoutError' || error.name === 'AbortError')
        ? 'The game server took too long to answer'
        : 'Could not reach the game server',
      { cause: error },
    );
  }
  let result: T & { error?: string };
  try {
    result = (await response.json()) as T & { error?: string };
  } catch (error) {
    throw new Error('The game server sent an answer this page cannot read', { cause: error });
  }
  if (!response.ok)
    throw new AccountRequestError(result.error ?? 'The request failed', response.status);
  return result;
}

/** A request the server turned down: its sentence, and the status it came with. */
export class AccountRequestError extends Error {
  readonly status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

/** A failed request as a player reads it: the sentence alone, without "Error:" before it. */
export function requestError(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

/** The rankings, asked for at most once a minute: the home screen opens often. */
let rankings: { at: number; users: Promise<Account[]> } | null = null;
const RANKINGS_TTL_MS = 60_000;

export function leaderboard(now = Date.now()): Promise<Account[]> {
  if (!rankings || now - rankings.at > RANKINGS_TTL_MS) {
    const users = accountRequest<{ users: Account[] }>('leaderboard').then((r) => r.users);
    const entry = { at: now, users };
    rankings = entry;
    // A failure is not kept: the next visit asks again.
    users.catch(() => {
      if (rankings === entry) rankings = null;
    });
  }
  return rankings.users;
}

/** Forgets the rankings, as after a game that may have changed them; and for tests. */
export function forgetLeaderboard(): void {
  rankings = null;
}
